/**
 * Pool API — garde d'actif en self-service (réservation, prise, restitution).
 *
 * Client UNIQUE du module `pool` (ADR-005, ADR-010 §3.1). Toute lecture ou
 * écriture du domaine passe par ici : aucun composant ne doit appeler
 * `/drivers/me/pool/*` à la main, ni toucher aux tables du module. C'est la
 * frontière côté frontend — l'équivalent d'`index.mjs` côté backend.
 *
 * Contrat serveur : `backend/modules/pool/README.md`.
 *
 * @module api/pool
 */

import { apiRequest } from './core.js';

/**
 * Codes d'erreur rendus par le module. L'app s'en sert pour choisir l'écran —
 * un message d'erreur brut ne dit pas à l'utilisateur quoi faire, le code si.
 * Ne jamais renommer sans versionner l'API.
 */
export const POOL_ERROR_CODES = {
  VALIDATION: 'VALIDATION',
  BAD_VOCABULARY: 'BAD_VOCABULARY',
  ODOMETER_REGRESSION: 'ODOMETER_REGRESSION',
  NOT_FOUND: 'NOT_FOUND',
  VEHICLE_BUSY: 'VEHICLE_BUSY',
  ALREADY_ACTIVE: 'ALREADY_ACTIVE',
  BAD_STATE: 'BAD_STATE',
  BLOCKING_DEFECT: 'BLOCKING_DEFECT',
  VEHICLE_TAKEN: 'VEHICLE_TAKEN',
  // Lot 3.3 — mise à disposition inter-unités.
  PROVISION_CONFLICT: 'PROVISION_CONFLICT',
  OUT_OF_PROVISION_WINDOW: 'OUT_OF_PROVISION_WINDOW',
};

/**
 * D'où vient un odomètre rendu par le module.
 * ⚠️ `vehicle_mileage` est FIGÉ entre deux pleins : ne jamais le présenter
 * comme une valeur vivante — l'afficher sans le dire, ou ne pas l'afficher.
 */
export const ODOMETER_SOURCE = {
  TELEMATICS: 'telematics',
  SHADOW: 'shadow',
  READING: 'reading',
  VEHICLE_MILEAGE: 'vehicle_mileage',
  UNKNOWN: 'unknown',
};

export const poolAPI = {
  /** L'écran d'accueil du pool, en un appel : garde active + réservations. */
  getHome: () => apiRequest('/drivers/me/pool'),

  /**
   * Le véhicule COURANT : celui de la garde en cours, sinon la dotation.
   * `source` vaut 'custody' ou 'assigned' — l'écran doit les distinguer.
   */
  getCurrentVehicle: () => apiRequest('/drivers/me/pool/current-vehicle'),

  /**
   * Disponibilité sur une fenêtre. `from` et `to` sont obligatoires (ISO 8601).
   * Chaque véhicule indisponible porte un `unavailable_reason` — l'afficher :
   * un écran qui grise sans expliquer envoie l'utilisateur au téléphone.
   */
  getAvailability: ({ from, to, vehicleClass } = {}) => {
    const qs = new URLSearchParams({ from, to, ...(vehicleClass ? { class: vehicleClass } : {}) }).toString();
    return apiRequest(`/drivers/me/pool/availability?${qs}`);
  },

  /** Réserver — par véhicule précis OU par classe. Confirmé d'emblée. */
  createReservation: (reservation) => apiRequest('/drivers/me/pool/reservation', {
    method: 'POST',
    body: JSON.stringify(reservation),
  }),

  getMyReservations: () => apiRequest('/drivers/me/pool/my-reservations'),

  /**
   * La « carte d'embarquement » d'une réservation (2026-09-14) : véhicule, où il
   * est garé, créneau élargi, trajet, défauts connus, carte carburant (last4),
   * QR de prise. `app_origin` comme pour l'étiquette : l'URL du QR est celle de
   * l'app, que le serveur ne voit pas derrière la gateway.
   */
  getBoardingPass: (reservationId) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const qs = origin ? `?app_origin=${encodeURIComponent(origin)}` : '';
    return apiRequest(`/drivers/me/pool/reservation/${encodeURIComponent(reservationId)}/boarding-pass${qs}`);
  },

  /**
   * Annuler AVANT la prise. Libère le véhicule pour le suivant à la seconde —
   * c'est ce qui rend le pool vivable quand un rendez-vous saute.
   */
  cancelReservation: (reservationId) => apiRequest(`/drivers/me/pool/reservation/${reservationId}/cancel`, {
    method: 'POST',
  }),

  /**
   * Prolonger. Le SERVEUR tranche : il refuse si le créneau supplémentaire
   * empiète sur la réservation suivante, marge de remise en état comprise.
   * Ne jamais présumer du succès pour afficher la nouvelle heure — la lire
   * dans la réponse.
   * @param {string} plannedEnd - nouvelle fin, ISO 8601
   */
  extendReservation: (reservationId, plannedEnd) => apiRequest(`/drivers/me/pool/reservation/${reservationId}/extend`, {
    method: 'POST',
    body: JSON.stringify({ planned_end: plannedEnd }),
  }),

  /**
   * Ce que le serveur sait avant la prise : véhicule, odomètre prérempli,
   * défauts déjà ouverts, et le vocabulaire fermé des défauts.
   * ⚠️ Utiliser CE vocabulaire pour les listes de saisie, jamais une copie
   * locale : corriger un libellé ne doit pas coûter une réinstallation.
   */
  getCheckoutPrefill: (reservationId, vehicleId = null) => {
    const qs = vehicleId ? `?vehicle_id=${encodeURIComponent(vehicleId)}` : '';
    return apiRequest(`/drivers/me/pool/reservation/${reservationId}/checkout-prefill${qs}`);
  },

  /**
   * La prise. Passer un `idempotency_key` : un brouillon rejoué après une
   * coupure réseau ne doit pas créer deux passations.
   */
  checkout: (reservationId, payload) => apiRequest(`/drivers/me/pool/reservation/${reservationId}/checkout`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  /** La restitution. Rend `distance_km` : ce que le conducteur a parcouru. */
  checkin: (reservationId, payload) => apiRequest(`/drivers/me/pool/reservation/${reservationId}/checkin`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

/**
 * Pool exceptions — la chaîne de traitement d'écarts du gestionnaire (vue
 * « Opérations », L6b). Contrat : `docs/implementations/feature/POOL_POC_API_CONTRACT_2026-09-11.md` §10.
 *
 * Distinct de `poolAPI` ci-dessus (self-service conducteur, `/drivers/me/pool/*`) :
 * ces routes sont côté gestionnaire (`managerGuard`), sous `/pool/exceptions`.
 */
export const poolExceptionsAPI = {
  /**
   * @param {Object} [filters]
   * @param {number|string} [filters.site_id]
   * @param {string} [filters.status] - open | acknowledged | resolved | expired
   * @param {string} [filters.kind]
   */
  list: ({ site_id, status, kind } = {}) => {
    const params = new URLSearchParams();
    if (site_id) params.set('site_id', site_id);
    if (status) params.set('status', status);
    if (kind) params.set('kind', kind);
    const qs = params.toString();
    return apiRequest(`/pool/exceptions${qs ? `?${qs}` : ''}`);
  },

  acknowledge: (id) => apiRequest(`/pool/exceptions/${id}/acknowledge`, {
    method: 'POST',
  }),

  resolve: (id, note) => apiRequest(`/pool/exceptions/${id}/resolve`, {
    method: 'POST',
    body: JSON.stringify({ note }),
  }),

  /** Déclenche une détection immédiate (même fonction que la tâche serveur). */
  detect: () => apiRequest('/pool/exceptions/detect', {
    method: 'POST',
  }),
};

/**
 * Pool custody — les gardes EN COURS et les gestes du gestionnaire dessus.
 *
 * Distinct des écarts : un écart est une tâche qui SE RÉSOUT, une garde est un
 * actif qui est DEHORS. La file d'écarts montre ce qui a déjà dérapé ; ceci
 * montre ce qui va déraper.
 *
 * ⚠️ `forceClose` et `block` sont des actions SENSIBLES sur un actif d'une
 * collectivité : motif obligatoire côté serveur, journalisées nominativement.
 * Ce sont les deux premières entrées de la liste blanche d'actions de
 * l'« opérateur d'exception » — ne jamais les fondre dans un appel générique
 * qui prendrait l'action en paramètre.
 */
export const poolCustodyAPI = {
  /**
   * @param {Object} [filters] - { site_id, limit }
   * `limit` : le serveur tronque à 200 sans le dire (`listActiveCustody`) — à
   * 900 véhicules, un écran qui ne le passe pas montre un parc « presque rentré ».
   */
  list: ({ site_id, limit } = {}) => {
    const params = new URLSearchParams();
    if (site_id) params.set('site_id', site_id);
    if (limit) params.set('_limit', String(limit));
    const qs = params.toString();
    return apiRequest(`/pool/custody${qs ? `?${qs}` : ''}`);
  },

  /**
   * Clôturer d'office. Libère la réservation — et ne prouve NI le retour du
   * véhicule, NI celui de la clé, NI son état.
   * @param {Object} payload - { reason (obligatoire), odometer_km?, immobilize? }
   */
  forceClose: (reservationId, payload) => apiRequest(`/pool/custody/${reservationId}/force-close`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),

  /**
   * Immobiliser un véhicule — geste séparé, jamais un effet de bord.
   *
   * Lot 1.2 (audit CD31 §Q3) : dater et qualifier le blocage. Rétro-compatible
   * — un simple texte (`reason`) reste accepté, un objet ajoute `started_at`,
   * `expected_end_at`, `cause`, `source_kind`, `source_id`, `owner_user_id`.
   * @param {Object|string} payload - { reason (obligatoire), started_at?,
   *   expected_end_at?, cause?, source_kind?, source_id?, owner_user_id? }
   */
  block: (vehicleId, payload) => apiRequest(`/pool/vehicles/${vehicleId}/block`, {
    method: 'POST',
    body: JSON.stringify(typeof payload === 'string' ? { reason: payload } : payload),
  }),

  /**
   * Résoudre UN défaut. Multi-blocage (lot 1.3) : un second défaut bloquant
   * resté ouvert sur le même véhicule continue de le retenir — aucun geste
   * supplémentaire n'est nécessaire ici, `openDefects` ne lit que les défauts
   * `status='open'`.
   * @param {Object} [payload] - { note? }
   */
  resolveDefect: (defectId, payload = {}) => apiRequest(`/pool/defects/${defectId}/resolve`, {
    method: 'POST',
    body: JSON.stringify(payload),
  }),
};

/**
 * Pool presence — « où est cet agent, avec quel véhicule ? » et « qui détient ce
 * véhicule ? » (2026-09-14, retour JGS : un chef d'U.T. doit pouvoir répondre
 * depuis la fiche, pas seulement depuis l'onglet Maintenant).
 *
 * La garde (pool, quelques heures) n'est PAS la dotation (`assigned_vehicle_id`,
 * durable) : l'endpoint rend les deux côte à côte, la fiche les distingue.
 * Contrat : `backend/modules/pool/presence_routes.mjs`.
 */
export const poolPresenceAPI = {
  /** `{ custody, next_reservation, assigned_vehicle }` — chacun `null` quand il n'y a rien. */
  forDriver: (driverId) => apiRequest(`/pool/drivers/${encodeURIComponent(driverId)}/presence`),
  /** `{ custody, next_reservation, assigned_driver }` — idem. */
  forVehicle: (vehicleId) => apiRequest(`/pool/vehicles/${encodeURIComponent(vehicleId)}/presence`),
};

/**
 * Pool availability — disponibilité du parc pour le GESTIONNAIRE (Q1/Q4 de
 * l'audit `docs/labo/2026-09-13_AUDIT_CAPACITE_METIER_CD31.md`).
 *
 * Distinct de `poolAPI.getAvailability` (self-service conducteur, une liste
 * sur le seul parc diesel) : ici on COMPTE, sur tout le parc actif, avec
 * drill-down par site et par catégorie — `backend/modules/pool/README.md`.
 */
export const poolAvailabilityAPI = {
  /**
   * @param {Object} params
   * @param {string} params.from - ISO 8601 avec décalage explicite
   * @param {string} params.to - ISO 8601 avec décalage explicite
   * @param {number|string} [params.siteId]
   * @param {string} [params.vehicleClass]
   */
  get: ({ from, to, siteId, vehicleClass } = {}) => {
    const params = new URLSearchParams({ from, to });
    if (siteId) params.set('site_id', siteId);
    if (vehicleClass) params.set('class', vehicleClass);
    return apiRequest(`/pool/availability?${params.toString()}`);
  },

  /** Q4 pour un véhicule précis : calculée / estimée / inconnue, jamais une date nue. */
  getNextFree: (vehicleId, after) => {
    const qs = after ? `?after=${encodeURIComponent(after)}` : '';
    return apiRequest(`/pool/vehicles/${vehicleId}/next-free${qs}`);
  },

  /** Q4 pour une classe : le premier véhicule libre, sinon le plus tôt calculé. */
  getNextFreeByClass: ({ vehicleClass, after, siteId } = {}) => {
    const params = new URLSearchParams({ class: vehicleClass });
    if (after) params.set('after', after);
    if (siteId) params.set('site_id', siteId);
    return apiRequest(`/pool/next-free?${params.toString()}`);
  },
};

/**
 * Pool schedule — la semaine du parc pour le GESTIONNAIRE (grille hebdomadaire,
 * `docs/labo/2026-09-13_GRILLE_HEBDO_ET_QR_PLAN.md` §A). Une requête pour toute la
 * fenêtre : les occupations de chaque véhicule, telles que le serveur les borne.
 */
export const poolScheduleAPI = {
  /**
   * @param {Object} params
   * @param {string} params.from - ISO 8601 avec décalage explicite
   * @param {string} params.to - ISO 8601 avec décalage explicite (31 jours au plus)
   * @param {number|string} [params.siteId]
   */
  get: ({ from, to, siteId } = {}) => {
    const params = new URLSearchParams({ from, to });
    if (siteId) params.set('site_id', siteId);
    return apiRequest(`/pool/schedule?${params.toString()}`);
  },
};

/**
 * Pool scan — le QR pare-brise (`docs/labo/2026-09-13_GRILLE_HEBDO_ET_QR_PLAN.md` §B).
 * `getLabel` pour le gestionnaire qui imprime ; `resolve` pour le conducteur qui scanne.
 * Le serveur relit et vérifie toujours le jeton — le client ne décide de rien.
 */
export const poolScanAPI = {
  /**
   * L'étiquette d'un véhicule : `{ token, url, svg }`. L'origine de l'application est envoyée
   * parce que, derrière la gateway, le serveur ne voit que son adresse interne.
   */
  getLabel: (vehicleId) => {
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    const qs = origin ? `?app_origin=${encodeURIComponent(origin)}` : '';
    return apiRequest(`/pool/vehicles/${vehicleId}/scan-label${qs}`);
  },
  /** Le véhicule scanné et le geste qui revient : `{ next_action, reservation, ... }`. */
  resolve: (token) => apiRequest(`/drivers/me/pool/scan/${encodeURIComponent(token)}`),
};

/**
 * Pool manager reservation — réserver pour le compte d'un tiers (lot 1.6,
 * Q5 de l'audit `docs/labo/2026-09-13_AUDIT_CAPACITE_METIER_CD31.md`).
 *
 * Distinct de `poolAPI.createReservation` (self-service conducteur) : ici le
 * GESTIONNAIRE réserve au guichet pour un agent sans smartphone. Même
 * enveloppe de mutation que le reste du module (`data.reservation`), même
 * contrat d'erreur (`code`, `data.next_actions`) — c'est la MÊME règle
 * `createReservation` qui a tranché côté serveur.
 */
export const poolManagerReservationAPI = {
  /**
   * @param {Object} reservation
   * @param {number|string} reservation.driver_id
   * @param {number|string} [reservation.vehicle_id]
   * @param {string} [reservation.vehicle_class]
   * @param {string} reservation.planned_start - ISO 8601 avec décalage explicite
   * @param {string} reservation.planned_end - ISO 8601 avec décalage explicite
   * @param {string} [reservation.destination_label]
   */
  reserveForDriver: (reservation) => apiRequest('/pool/reservations', {
    method: 'POST',
    body: JSON.stringify(reservation),
  }),

  /**
   * Transférer une réservation `confirmed` à un autre conducteur, sans perdre
   * le créneau (lot 2.4, `backend/modules/pool/reassign_service.mjs`). Une
   * garde déjà PRISE ne se réassigne pas — elle se rend puis se re-réserve :
   * le serveur refuse `BAD_STATE`, et l'écran ne propose pas le geste sur un
   * bloc « sorti ». `DRIVER_BUSY` si le nouveau conducteur est déjà pris sur
   * le créneau ; 404 hors périmètre. Même contrat d'erreur (`code`,
   * `data.next_actions`).
   * @param {number|string} reservationId
   * @param {number|string} newDriverId
   */
  reassign: (reservationId, newDriverId) => apiRequest(`/pool/reservations/${encodeURIComponent(reservationId)}/reassign`, {
    method: 'POST',
    body: JSON.stringify({ new_driver_id: newDriverId }),
  }),
};

/**
 * Pool provisions — la MISE À DISPOSITION temporaire inter-unités (lot 3.3,
 * `backend/modules/pool/provision_service.mjs`, README §« La mise à disposition »).
 *
 * ⚠️ `from`, `to` et `at` sont des dates **civiles** `AAAA-MM-JJ`, bornes
 * incluses — jamais un horodatage : une mise à disposition est un fait de
 * calendrier, pas un instant (ADR-013 §3). Le serveur refuse tout ce qui porte
 * une heure. Pour un `<input type="date">`, la valeur est déjà au bon format :
 * ne surtout pas la passer par `new Date().toISOString()`, qui décalerait d'un
 * jour selon le fuseau.
 *
 * L'attache du véhicule NE BOUGE PAS. Refus à afficher tels quels :
 * `PROVISION_CONFLICT` (409, `data.conflicts` = les réservations à réassigner ou
 * annuler d'abord — jamais d'éviction) et `VALIDATION`.
 */
export const poolProvisionAPI = {
  /**
   * @param {Object} provision
   * @param {number|string} provision.vehicle_id
   * @param {number|string} provision.to_site_id - l'unité BÉNÉFICIAIRE (≠ attache)
   * @param {string} provision.from - date civile AAAA-MM-JJ
   * @param {string} provision.to - date civile AAAA-MM-JJ
   * @param {string} [provision.reason]
   */
  create: (provision) => apiRequest('/pool/provisions', {
    method: 'POST',
    body: JSON.stringify(provision),
  }),

  /**
   * Ce qu'on a prêté ET ce qu'on nous a prêté (le serveur tranche la visibilité).
   * @param {Object} [params]
   * @param {number|string} [params.vehicleId]
   * @param {number|string} [params.siteId]
   * @param {boolean} [params.activeOnly] - seulement les fenêtres courantes
   */
  list: ({ vehicleId, siteId, activeOnly } = {}) => {
    const params = new URLSearchParams();
    if (vehicleId) params.set('vehicle_id', vehicleId);
    if (siteId) params.set('site_id', siteId);
    if (activeOnly) params.set('active', '1');
    const qs = params.toString();
    return apiRequest(`/pool/provisions${qs ? `?${qs}` : ''}`);
  },

  /**
   * Retour anticipé : RACCOURCIT la fenêtre, ne la supprime pas.
   * @param {number|string} provisionId
   * @param {string} [at] - date civile AAAA-MM-JJ ; défaut serveur = aujourd'hui
   */
  end: (provisionId, at = null) => apiRequest(`/pool/provisions/${encodeURIComponent(provisionId)}/end`, {
    method: 'POST',
    body: JSON.stringify(at ? { at } : {}),
  }),
};

export default poolAPI;
